import React, { useCallback, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dropdown,
  Icon,
  Segment,
  Step,
} from 'semantic-ui-react';

import {
  accessUtil,
  authUtil,
  featureToggleUtil,
  uxAnalyticsUtil,
} from '../../utils';
import { CenteredContent } from 'src/style';
import BetaIcon from 'src/components/BetaIcon';
import useFeedbackDigest, {
  FeedbackDigestReport,
  FeedbackDigestStatus,
  FeedbackGlance,
  ReactionMix,
} from 'src/hooks/useFeedbackDigest';
import { useCredits, useCreditsConfig } from 'src/hooks/useCredits';
import { useFeatureFlags } from 'src/hooks/useFeatureFlags';
import CreditBalanceChip from 'src/components/credits/CreditBalanceChip';
import OutOfCreditsModal from 'src/components/credits/OutOfCreditsModal';

const DEFAULT_DIGEST_COST = 50;

const MyCenteredContent = styled(CenteredContent)`
  padding-left: 1rem;
  padding-right: 1rem;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  flex: 1;
  font-size: 1.5em;
  font-weight: bold;
  margin: 0;
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;

  label {
    font-weight: 600;
    font-size: 0.9em;
  }
`;

// ── free "at a glance" card ──
const GlanceCard = styled(Segment)`
  border-left: 3px solid #2b8a8a !important;
  background: var(--br-raised-surface) !important;
`;

const FreePill = styled.span`
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.05em;
  color: #fff;
  background: #2b8a8a;
  padding: 3px 9px;
  border-radius: 6px;
  text-transform: uppercase;
  margin-left: auto;
`;

const StatGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin: 12px 0 18px;
`;

const StatBox = styled.div`
  min-width: 120px;
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 22px;
  font-weight: 800;
  color: var(--br-text);
`;

const StatLabel = styled.div`
  font-size: 11.5px;
  color: var(--br-muted);
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
`;

const GlanceSubhead = styled.div`
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--br-muted);
  margin: 16px 0 8px;
`;

const ReactionBarTrack = styled.div`
  display: flex;
  height: 14px;
  border-radius: 7px;
  overflow: hidden;
  background: var(--br-raised-surface);
  margin-top: 6px;
`;

const ReactionSegment = styled.div<{ $color: string; $pct: number }>`
  width: ${props => props.$pct}%;
  background: ${props => props.$color};
`;

const ReactionLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
  font-size: 11.5px;
  color: var(--br-muted);
`;

const LegendDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$color};
  margin-right: 4px;
`;

const BarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 12.5px;
`;

const BarLabel = styled.span`
  flex: none;
  width: 140px;
  color: var(--br-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BarTrack = styled.div`
  flex: 1;
  background: var(--br-raised-surface);
  border-radius: 5px;
  height: 10px;
  overflow: hidden;
`;

const BarFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${props => props.$pct}%;
  background: #2b8a8a;
  border-radius: 5px;
`;

const BarCount = styled.span`
  flex: none;
  width: 26px;
  text-align: right;
  color: #8a9099;
  font-weight: 700;
`;

const EngagementChart = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 64px;
  margin-top: 8px;
`;

const EngagementBar = styled.div<{ $pct: number }>`
  flex: 1;
  min-width: 4px;
  height: ${props => Math.max(props.$pct, 3)}%;
  background: #7fc4c0;
  border-radius: 3px 3px 0 0;
`;

const SlowNote = styled.div`
  color: #c0392b;
  font-size: 12px;
  margin-top: 8px;
  font-weight: 600;
`;

// ── locked preview teaser (below the run CTA) ──
const LockedPreview = styled.div`
  position: relative;
  margin-top: 16px;
  border-radius: 14px;
  overflow: hidden;
`;

const BlurredContent = styled.div`
  filter: blur(4px);
  pointer-events: none;
  user-select: none;
  padding: 4px;
`;

const LockOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.6);
  text-align: center;
`;

// ── report styling, mirroring the AI Editor design language ──
const Masthead = styled.div`
  background: linear-gradient(135deg, #12303f, #1c4b53);
  color: #fff;
  padding: 26px 30px;
  border-radius: 14px;
`;

const MastheadEyebrow = styled.div`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: #9fd6d0;
`;

const StatLine = styled.div`
  display: flex;
  gap: 18px;
  margin-top: 14px;
  flex-wrap: wrap;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);

  b {
    color: #fff;
    font-weight: 800;
  }
`;

const SectionCard = styled.div<{ $open?: boolean }>`
  background: var(--br-surface);
  border: 1px solid ${props => (props.$open ? '#f0b862' : '#e3e6ea')};
  border-radius: 14px;
  margin-top: 14px;
  overflow: hidden;
`;

const SectionHead = styled.button`
  width: 100%;
  background: var(--br-surface);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  font-size: 15.5px;
  font-weight: 800;
  color: var(--br-text);
  text-align: left;
  font-family: inherit;
`;

const SectionTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SectionIcon = styled.span<{ $bg: string; $color: string }>`
  flex: none;
  width: 24px;
  height: 24px;
  border-radius: 7px;
  background: ${props => props.$bg};
  color: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 900;
`;

const CountPill = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #8a9099;
  background: var(--br-raised-surface);
  padding: 2px 9px;
  border-radius: 20px;
`;

const ReaderPill = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #00867e;
  background: var(--br-raised-surface);
  padding: 2px 9px;
  border-radius: 20px;
`;

const Chevron = styled.span<{ $open?: boolean }>`
  font-size: 13px;
  color: #b0b6bd;
  transition: transform 0.2s;
  transform: rotate(${props => (props.$open ? '0deg' : '-90deg')});
`;

const SectionBody = styled.div`
  padding: 2px 18px 18px;
  font-size: 13.5px;
  line-height: 1.6;
  color: var(--br-muted);
`;

const FixBox = styled.div`
  background: var(--br-raised-surface);
  border: 1px solid #ffe1b8;
  border-radius: 11px;
  padding: 14px 16px;
  margin-top: 12px;
`;

const FixCap = styled.div`
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.06em;
  color: #b8690a;
  margin-bottom: 8px;
`;

const FixRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 8px;
`;

const FixNum = styled.span`
  flex: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ff8c00;
  color: #fff;
  font-weight: 900;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FixText = styled.span`
  font-size: 13.5px;
  line-height: 1.5;
  color: var(--br-text);
`;

const ItemCard = styled.div`
  border: 1px solid #e7eaed;
  border-radius: 11px;
  padding: 13px 15px;
  margin-bottom: 12px;
`;

const ItemHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 7px;
  flex-wrap: wrap;
`;

const RankBadge = styled.span`
  flex: none;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: #141d2b;
  color: #fff;
  font-weight: 900;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ItemTitle = styled.span`
  font-weight: 800;
  font-size: 14.5px;
  flex: 1;
  color: var(--br-text);
`;

const SENTIMENT_COLORS: Record<string, [string, string, string]> = {
  positive: ['#e6f7ee', '#1a7f4b', 'POSITIVE'],
  negative: ['#fdecec', '#c0392b', 'NEEDS WORK'],
  mixed: ['#fff3e2', '#c26f00', 'MIXED'],
};

const SentimentChip = styled.span<{ $sentiment: string }>`
  background: ${props =>
    (SENTIMENT_COLORS[props.$sentiment] || SENTIMENT_COLORS.mixed)[0]};
  color: ${props =>
    (SENTIMENT_COLORS[props.$sentiment] || SENTIMENT_COLORS.mixed)[1]};
  font-weight: 800;
  font-size: 10.5px;
  letter-spacing: 0.05em;
  padding: 3px 9px;
  border-radius: 6px;
  text-transform: uppercase;
`;

const SEVERITY_COLORS: Record<number, [string, string, string]> = {
  3: ['#fdecec', '#c0392b', 'HIGH'],
  2: ['#fff3e2', '#c26f00', 'MEDIUM'],
  1: ['#f2f4f7', '#7b828b', 'LOW'],
};

const SevChip = styled.span<{ $level: number }>`
  background: ${props => (SEVERITY_COLORS[props.$level] || SEVERITY_COLORS[1])[0]};
  color: ${props => (SEVERITY_COLORS[props.$level] || SEVERITY_COLORS[1])[1]};
  font-weight: 800;
  font-size: 10.5px;
  letter-spacing: 0.05em;
  padding: 3px 9px;
  border-radius: 6px;
  text-transform: uppercase;
`;

const NET_COLORS: Record<string, string> = {
  positive: '#1a7f4b',
  negative: '#c0392b',
  neutral: '#8a9099',
};

const NetDot = styled.span<{ $net: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => NET_COLORS[props.$net] || NET_COLORS.neutral};
  margin-right: 4px;
`;

const QuoteRow = styled.div`
  display: flex;
  gap: 9px;
  margin-top: 6px;
`;

const QuoteText = styled.span`
  font-style: italic;
  border-left: 2px solid #e3e6ea;
  padding-left: 9px;
  font-size: 13px;
  color: var(--br-muted);
`;

const NoteCard = styled.div<{ $accent: string; $bg: string }>`
  border-radius: 11px;
  padding: 12px 14px;
  background: ${props => props.$bg};
  border: 1px solid ${props => props.$accent};
  margin-top: 10px;
`;

const NoteCap = styled.div<{ $color: string }>`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  color: ${props => props.$color};
  margin-bottom: 6px;
`;

const NoteCardsRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  > * {
    flex: 1;
    min-width: 220px;
  }
`;

const FooterNote = styled.p`
  color: #8a9099;
  font-size: 13px;
  margin-top: 1.5rem;
  line-height: 1.6;
`;

const RUNNING_STATUSES: FeedbackDigestStatus[] = [
  'pending',
  'aggregating',
  'summarizing',
  'synthesizing',
];

const PHASES: FeedbackDigestStatus[] = [
  'aggregating',
  'summarizing',
  'synthesizing',
  'completed',
];

const PHASE_LABELS: Record<string, string> = {
  aggregating: 'Aggregating feedback',
  summarizing: 'Summarizing chapters',
  synthesizing: 'Synthesizing digest',
};

const REACTION_COLORS: { key: keyof ReactionMix; color: string }[] = [
  { key: 'LIKE', color: '#63c76b' },
  { key: 'LOVE', color: '#e8608a' },
  { key: 'LAUGH', color: '#e8d020' },
  { key: 'SURPRISE', color: '#38c6b8' },
  { key: 'SAD', color: '#6aa9d8' },
  { key: 'ANGRY', color: '#e85c5c' },
];

/**
 * The free "Feedback at a glance" card. Always rendered — computed from raw
 * comment/reaction counts, never gated behind credits.
 */
const GlanceView: React.FC<{ glance?: FeedbackGlance }> = ({ glance }) => {
  const { t } = useTranslation();

  const commentCount = glance?.commentCount ?? 0;
  const readerCount = glance?.readerCount ?? 0;
  const finishedCount = glance?.finishedCount ?? 0;
  const reviewCount = glance?.reviewCount ?? 0;
  const readyToPublishCount = glance?.readyToPublishCount ?? 0;
  const avgOverallRating = glance?.avgOverallRating ?? null;
  const reactionTotal = glance?.reactionTotal ?? 0;
  const reactionMix = glance?.reactionMix;
  const topChapters = glance?.topChapters || [];
  const engagementByChapter = glance?.engagementByChapter || [];
  const slowestStretch = glance?.slowestStretch;

  // reduce (not spread) so a very long manuscript's chapter array can't blow
  // the argument-count limit and throw a RangeError.
  const maxTopChapter = topChapters.reduce((m, c) => Math.max(m, c.count), 1);
  const maxEngagement = engagementByChapter.reduce(
    (m, c) => Math.max(m, c.count),
    1
  );

  return (
    <GlanceCard>
      <p>Totals exclude explicitly AI-generated critiques. Undeclared older feedback is included without verification.</p>
      <HeaderRow>
        <strong>{t('FeedbackAtAGlance', 'Feedback at a glance')}</strong>
        <FreePill>{t('FreeNoCredits', 'FREE · NO CREDITS')}</FreePill>
      </HeaderRow>

      <StatGrid>
        <StatBox>
          <StatValue>{commentCount}</StatValue>
          <StatLabel>{t('Comments', 'Comments')}</StatLabel>
        </StatBox>
        <StatBox>
          <StatValue>{readerCount}</StatValue>
          <StatLabel>
            {t('BetaReadersNFinished', '{{n}} finished', {
              n: finishedCount,
            })}
          </StatLabel>
        </StatBox>
        <StatBox>
          <StatValue>
            {avgOverallRating != null ? avgOverallRating.toFixed(1) : '—'}
            <span style={{ fontSize: '13px', fontWeight: 600 }}>/5</span>
          </StatValue>
          <StatLabel>{t('AvgOverallRating', 'Avg. overall rating')}</StatLabel>
        </StatBox>
        <StatBox>
          <StatValue>
            {readyToPublishCount}
            <span style={{ fontSize: '13px', fontWeight: 600 }}>
              /{reviewCount}
            </span>
          </StatValue>
          <StatLabel>{t('ReadyToPublish', 'Ready to publish')}</StatLabel>
        </StatBox>
      </StatGrid>

      <GlanceSubhead>{t('ReactionMix', 'Reaction mix')}</GlanceSubhead>
      <ReactionBarTrack>
        {REACTION_COLORS.map(({ key, color }) => {
          const value = reactionMix?.[key] ?? 0;
          const pct = reactionTotal > 0 ? (value / reactionTotal) * 100 : 0;
          return pct > 0 ? (
            <ReactionSegment key={key} $color={color} $pct={pct} />
          ) : null;
        })}
      </ReactionBarTrack>
      <ReactionLegend>
        {REACTION_COLORS.map(({ key, color }) => (
          <span key={key}>
            <LegendDot $color={color} />
            {key} ({reactionMix?.[key] ?? 0})
          </span>
        ))}
      </ReactionLegend>

      {!!topChapters.length && (
        <>
          <GlanceSubhead>
            {t('MostCommentedChapters', 'Most-commented chapters')}
          </GlanceSubhead>
          {topChapters.map(ch => (
            <BarRow key={ch.index}>
              <BarLabel title={ch.title}>{ch.title}</BarLabel>
              <BarTrack>
                <BarFill $pct={(ch.count / maxTopChapter) * 100} />
              </BarTrack>
              <BarCount>{ch.count}</BarCount>
            </BarRow>
          ))}
        </>
      )}

      {!!engagementByChapter.length && (
        <>
          <GlanceSubhead>
            {t('ReaderEngagement', 'Reader engagement')}
          </GlanceSubhead>
          <EngagementChart>
            {engagementByChapter.map(ch => (
              <EngagementBar
                key={ch.index}
                $pct={(ch.count / maxEngagement) * 100}
                title={`${ch.title}: ${ch.count}`}
              />
            ))}
          </EngagementChart>
          {!!slowestStretch && (
            <SlowNote>
              {t('SlowestStretch', 'Slowest stretch: {{stretch}}', {
                stretch: slowestStretch,
              })}
            </SlowNote>
          )}
        </>
      )}
    </GlanceCard>
  );
};

/**
 * The rendered digest report (masthead + collapsible sections). Extracted
 * so the exact same renderer can be reused elsewhere without drift.
 */
export const FeedbackDigestReportView: React.FC<{
  report: FeedbackDigestReport;
  statsCommentCount?: number;
  statsReaderCount?: number;
  statsAvgOverallRating?: number | null;
  creditsCharged?: number;
  onDownload?: () => void;
}> = ({
  report,
  statsCommentCount,
  statsReaderCount,
  statsAvgOverallRating,
  creditsCharged,
  onDownload,
}) => {
  const { t } = useTranslation();
  const [openSections, setOpenSections] = useState<Record<number, boolean>>({
    0: true,
    1: true,
  });
  const toggleSection = (index: number) =>
    setOpenSections(prev => ({ ...prev, [index]: !prev[index] }));

  const overallRating = report.ratings?.overall ?? statsAvgOverallRating ?? null;

  return (
    <>
      <Masthead>
        <HeaderRow>
          <MastheadEyebrow>
            {t('FeedbackDigestComplete', 'FEEDBACK DIGEST · COMPLETE')} · AI-GENERATED
          </MastheadEyebrow>
          {onDownload && (
            <Button
              size="tiny"
              basic
              inverted
              icon="download"
              content={t('DownloadMarkdown', 'Download')}
              onClick={onDownload}
              style={{ marginLeft: 'auto' }}
            />
          )}
        </HeaderRow>
        <StatLine>
          <span>
            <b>{statsCommentCount ?? 0}</b> {t('Comments', 'comments')}
          </span>
          <span>
            <b>{statsReaderCount ?? 0}</b> {t('Readers', 'readers')}
          </span>
          <span>
            <b>{overallRating != null ? overallRating.toFixed(1) : '—'}</b>{' '}
            {t('AvgRating', 'avg rating')}
          </span>
          <span>
            <b>{(report.themes || []).length}</b> {t('Themes', 'themes')}
          </span>
          <span>
            <b>{(report.issues || []).length}</b> {t('Issues', 'issues')}
          </span>
          {!!creditsCharged && (
            <span style={{ marginLeft: 'auto' }}>
              {t('ThisReportUsedNCredits', 'Used {{n}} credits', {
                n: creditsCharged,
              })}
            </span>
          )}
        </StatLine>
      </Masthead>

      {/* Executive summary */}
      <SectionCard $open={!!openSections[0]}>
        <SectionHead onClick={() => toggleSection(0)}>
          <SectionTitle>
            <SectionIcon $bg="#eef2f7" $color="#41505f">
              ✦
            </SectionIcon>
            {t('ExecutiveSummary', 'Executive summary')}
          </SectionTitle>
          <Chevron $open={!!openSections[0]}>▼</Chevron>
        </SectionHead>
        {!!openSections[0] && (
          <SectionBody>
            <p>{report.execSummary}</p>
            {!!(report.priorities || []).length && (
              <FixBox>
                <FixCap>
                  {t('FixTheseThreeFirst', 'FIX THESE THREE FIRST')}
                </FixCap>
                {(report.priorities || []).map(item => (
                  <FixRow key={item.n}>
                    <FixNum>{item.n}</FixNum>
                    <FixText>{item.text}</FixText>
                  </FixRow>
                ))}
              </FixBox>
            )}
          </SectionBody>
        )}
      </SectionCard>

      {/* What readers agree on */}
      <SectionCard $open={!!openSections[1]}>
        <SectionHead onClick={() => toggleSection(1)}>
          <SectionTitle>
            <SectionIcon $bg="#eafbfa" $color="#00867e">
              ☺
            </SectionIcon>
            {t('WhatReadersAgreeOn', 'What readers agree on')}
            <CountPill>
              {(report.themes || []).length} {t('Themes', 'themes')}
            </CountPill>
          </SectionTitle>
          <Chevron $open={!!openSections[1]}>▼</Chevron>
        </SectionHead>
        {!!openSections[1] && (
          <SectionBody>
            {(report.themes || []).map((theme, i) => (
              <ItemCard key={i}>
                <ItemHead>
                  <ItemTitle>{theme.title}</ItemTitle>
                  <SentimentChip $sentiment={theme.sentiment}>
                    {(SENTIMENT_COLORS[theme.sentiment] ||
                      SENTIMENT_COLORS.mixed)[2]}
                  </SentimentChip>
                  {theme.readerLabel && (
                    <ReaderPill>{theme.readerLabel}</ReaderPill>
                  )}
                </ItemHead>
                {(theme.evidence || []).map((ev, j) => (
                  <QuoteRow key={j}>
                    <CountPill>{ev.tag}</CountPill>
                    <QuoteText>&ldquo;{ev.text}&rdquo;</QuoteText>
                  </QuoteRow>
                ))}
              </ItemCard>
            ))}
          </SectionBody>
        )}
      </SectionCard>

      {/* Most-raised issues */}
      <SectionCard $open={!!openSections[2]}>
        <SectionHead onClick={() => toggleSection(2)}>
          <SectionTitle>
            <SectionIcon $bg="#fdecec" $color="#c0392b">
              !
            </SectionIcon>
            {t('MostRaisedIssues', 'Most-raised issues')}
            <CountPill>
              {(report.issues || []).length} · {t('Ranked', 'ranked')}
            </CountPill>
          </SectionTitle>
          <Chevron $open={!!openSections[2]}>▼</Chevron>
        </SectionHead>
        {!!openSections[2] && (
          <SectionBody>
            {(report.issues || []).map(issue => (
              <ItemCard key={issue.rank}>
                <ItemHead>
                  <RankBadge>{issue.rank}</RankBadge>
                  <ItemTitle>{issue.title}</ItemTitle>
                  <SevChip $level={issue.severity}>
                    {(SEVERITY_COLORS[issue.severity] || SEVERITY_COLORS[1])[2]}
                  </SevChip>
                  {issue.readerLabel && (
                    <ReaderPill>{issue.readerLabel}</ReaderPill>
                  )}
                </ItemHead>
                {issue.chapters && <div>{issue.chapters}</div>}
                {(issue.evidence || []).map((ev, j) => (
                  <QuoteRow key={j}>
                    <CountPill>{ev.tag}</CountPill>
                    <QuoteText>&ldquo;{ev.text}&rdquo;</QuoteText>
                  </QuoteRow>
                ))}
              </ItemCard>
            ))}
          </SectionBody>
        )}
      </SectionCard>

      {/* Per-chapter hotspots */}
      <SectionCard $open={!!openSections[3]}>
        <SectionHead onClick={() => toggleSection(3)}>
          <SectionTitle>
            <SectionIcon $bg="#eef2f7" $color="#41505f">
              ▤
            </SectionIcon>
            {t('PerChapterHotspots', 'Per-chapter hotspots')}
            <CountPill>{(report.hotspots || []).length}</CountPill>
          </SectionTitle>
          <Chevron $open={!!openSections[3]}>▼</Chevron>
        </SectionHead>
        {!!openSections[3] && (
          <SectionBody>
            {(report.hotspots || []).map((hotspot, i) => (
              <ItemCard key={i}>
                <ItemHead>
                  <CountPill>{hotspot.ch}</CountPill>
                  <ItemTitle>{hotspot.title}</ItemTitle>
                  <span>
                    <NetDot $net={hotspot.net} />
                    {hotspot.net}
                  </span>
                  <CountPill>
                    {t('NComments', '{{n}} comments', { n: hotspot.count })}
                  </CountPill>
                </ItemHead>
                {hotspot.note && <div>{hotspot.note}</div>}
              </ItemCard>
            ))}
          </SectionBody>
        )}
      </SectionCard>

      {/* Engagement & drop-off */}
      <SectionCard $open={!!openSections[4]}>
        <SectionHead onClick={() => toggleSection(4)}>
          <SectionTitle>
            <SectionIcon $bg="#f2f4f7" $color="#7b828b">
              ≈
            </SectionIcon>
            {t('EngagementAndDropOff', 'Engagement & drop-off')}
          </SectionTitle>
          <Chevron $open={!!openSections[4]}>▼</Chevron>
        </SectionHead>
        {!!openSections[4] && (
          <SectionBody>
            {report.engagement?.note && <p>{report.engagement.note}</p>}
            <NoteCardsRow>
              {report.engagement?.dropOff && (
                <NoteCard $accent="#f6c2c2" $bg="#fdecec">
                  <NoteCap $color="#c0392b">
                    {t('DropOffPoint', 'DROP-OFF POINT')}
                  </NoteCap>
                  <div>{report.engagement.dropOff}</div>
                </NoteCard>
              )}
              {report.engagement?.enthusiasm && (
                <NoteCard $accent="#b7e3c9" $bg="#e6f7ee">
                  <NoteCap $color="#1a7f4b">
                    {t('EnthusiasmSpike', 'ENTHUSIASM SPIKE')}
                  </NoteCap>
                  <div>{report.engagement.enthusiasm}</div>
                </NoteCard>
              )}
            </NoteCardsRow>
          </SectionBody>
        )}
      </SectionCard>

      {/* Ratings summary */}
      <SectionCard $open={!!openSections[5]}>
        <SectionHead onClick={() => toggleSection(5)}>
          <SectionTitle>
            <SectionIcon $bg="#fff3e2" $color="#c26f00">
              ★
            </SectionIcon>
            {t('RatingsSummary', 'Ratings summary')}
          </SectionTitle>
          <Chevron $open={!!openSections[5]}>▼</Chevron>
        </SectionHead>
        {!!openSections[5] && (
          <SectionBody>
            <StatGrid>
              <StatBox>
                <StatValue>
                  {report.ratings?.overall != null
                    ? report.ratings.overall.toFixed(1)
                    : '—'}
                  <span style={{ fontSize: '13px', fontWeight: 600 }}>/5</span>
                </StatValue>
                <StatLabel>{t('Overall', 'Overall')}</StatLabel>
              </StatBox>
              <StatBox>
                <StatValue>
                  {report.ratings?.plot != null
                    ? report.ratings.plot.toFixed(1)
                    : '—'}
                  <span style={{ fontSize: '13px', fontWeight: 600 }}>/5</span>
                </StatValue>
                <StatLabel>{t('Plot', 'Plot')}</StatLabel>
              </StatBox>
              <StatBox>
                <StatValue>
                  {report.ratings?.grammar != null
                    ? report.ratings.grammar.toFixed(1)
                    : '—'}
                  <span style={{ fontSize: '13px', fontWeight: 600 }}>/5</span>
                </StatValue>
                <StatLabel>{t('Grammar', 'Grammar')}</StatLabel>
              </StatBox>
              <StatBox>
                <StatValue>
                  {report.ratings?.readyToPublishCount ?? 0}
                  <span style={{ fontSize: '13px', fontWeight: 600 }}>
                    /{report.ratings?.reviewCount ?? 0}
                  </span>
                </StatValue>
                <StatLabel>{t('ReadyToPublish', 'Ready to publish')}</StatLabel>
              </StatBox>
            </StatGrid>
          </SectionBody>
        )}
      </SectionCard>

      <FooterNote>
        {t(
          'FeedbackDigestFooterNote',
          'Every claim is grounded in what your readers actually wrote — counts are computed from their comments, not guessed. Readers are pseudonymized; quotes stay verbatim.'
        )}
      </FooterNote>
    </>
  );
};

const BookFeedbackDigest: React.FC = () => {
  const { t, i18n } = useTranslation();

  const currentBook = useSelector((state: any) => state.book.currentBook);
  const userProfile = useSelector((state: any) => state.user.userProfile);

  const contentList = currentBook?.content || [];
  const latestContentId = contentList.at(-1)?.id;
  const [selectedContentId, setSelectedContentId] = useState<
    string | undefined
  >(latestContentId);

  const contentId = selectedContentId || latestContentId;

  const { feedbackDigestQuery, startFeedbackDigest, isStarting } =
    useFeedbackDigest({ bookId: currentBook?._id, contentId });

  const { data: credits } = useCredits();
  const { data: config } = useCreditsConfig();
  const { flags, isLoading: flagsLoading } = useFeatureFlags();
  const aiEditorEnabled =
    flags?.ai_editor === true ||
    featureToggleUtil.hasFeatureAccess(userProfile, 'ai_editor');

  const [modalOpen, setModalOpen] = useState(false);

  const feedbackDigest = feedbackDigestQuery.data?.feedbackDigest;
  const glance = feedbackDigestQuery.data?.glance;
  const status = feedbackDigest?.status;

  const canEdit = useCallback(
    () => accessUtil.isAllowed(currentBook?.access, ['edit']),
    [currentBook?.access]
  );
  const editable = canEdit();

  const digestCost =
    config?.features?.feedback_digest?.credits ?? DEFAULT_DIGEST_COST;
  const balance = credits?.balance ?? 0;
  // Stay optimistic until the balance actually loads, so the run button isn't
  // briefly replaced by a false "not enough credits" upsell on every page load.
  const hasEnough = credits === undefined || balance >= digestCost;

  const isRunning = !!status && RUNNING_STATUSES.includes(status);
  const isCompleted = status === 'completed' && !!feedbackDigest?.report;
  const isError = status === 'error';
  const showGlanceCta = !isRunning && !isCompleted;

  const startRun = useCallback(async () => {
    uxAnalyticsUtil.trackEvent({
      category: 'AI',
      action: 'clicked-run-feedback-digest',
    });
    await authUtil.getFreshIdToken();
    const result = await startFeedbackDigest({
      reportLanguage: (i18n.language || 'en').split('-')[0],
    });
    if (result?.status === 402) {
      setModalOpen(true);
      return;
    }
    feedbackDigestQuery.refetch();
  }, [startFeedbackDigest, feedbackDigestQuery, i18n.language]);

  const downloadMarkdown = () => {
    const markdown = feedbackDigest?.reportMarkdown ? 'AI-generated summary of reader feedback. AI-generated critiques are excluded from new summaries. Undeclared legacy feedback is not verified as human.\n\n' + feedbackDigest.reportMarkdown : undefined;
    if (!markdown) {
      return;
    }
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentBook?.title || 'feedback-digest'}.md`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (!currentBook) {
    return null;
  }

  if (!flagsLoading && !aiEditorEnabled) {
    return (
      <MyCenteredContent>
        <Segment>
          {t('FeatureNotAvailable', 'This feature is not available.')}
        </Segment>
      </MyCenteredContent>
    );
  }

  const errorMessage = (() => {
    if (feedbackDigest?.error === 'INSUFFICIENT_CREDITS') {
      return t(
        'InsufficientCreditsMessage',
        "You don't have enough credits to run this digest."
      );
    }
    if (feedbackDigest?.error === 'NO_FEEDBACK') {
      return t(
        'NoFeedbackYet',
        'No reader feedback on this version yet — invite readers or wait for comments before running a digest.'
      );
    }
    return t(
      'FeedbackDigestSomethingWentWrong',
      'Something went wrong — please try again.'
    );
  })();

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{`${t('FeedbackDigest', 'Feedback Digest')} · ${currentBook.title}`}</title>
      </Helmet>
      <MyCenteredContent>
        <HeaderRow>
          <Title>
            {t('FeedbackDigest', 'Feedback Digest')} <BetaIcon />
          </Title>
          <CreditBalanceChip usageHref="/account/subscription" />
        </HeaderRow>

        {/* Content version selector */}
        <FieldRow style={{ marginTop: '1rem' }}>
          <label>{t('Version', 'Version')}</label>
          <Dropdown
            selection
            value={contentId}
            options={contentList.map((c: any, i: number) => ({
              key: c.id,
              value: c.id,
              text: c.title || `${t('Version', 'Version')} ${i + 1}`,
            }))}
            onChange={(_, { value }) => setSelectedContentId(value as string)}
          />
        </FieldRow>

        {/* Always-on free glance */}
        <GlanceView glance={glance} />

        {/* Idle / error state: run CTA + locked preview */}
        {showGlanceCta && (
          <Segment style={{ marginTop: '14px' }}>
            {isError && (
              <div
                className="ui negative message"
                style={{ marginBottom: '12px' }}
              >
                <Icon name="exclamation triangle" /> {errorMessage}
              </div>
            )}

            {editable ? (
              hasEnough ? (
                <Button
                  primary
                  loading={isStarting}
                  onClick={startRun}
                  content={t(
                    'RunFeedbackDigest',
                    'Run Feedback Digest — {{n}} credits',
                    { n: digestCost }
                  )}
                />
              ) : (
                <div>
                  <p>
                    {t(
                      'NotEnoughCredits',
                      'Not enough credits — you have {{balance}}, a digest costs {{required}}.',
                      { balance, required: digestCost }
                    )}
                  </p>
                  <Button
                    color="orange"
                    onClick={() => setModalOpen(true)}
                    content={t('SeeOptions', 'See options')}
                  />
                </div>
              )
            ) : null}

            <p style={{ marginTop: '8px', color: '#666' }}>
              {t(
                'FeedbackDigestRunDescription',
                'Aggregates every comment, reaction and review on this version into one synthesis — themes readers agree on, the most-raised issues, and where engagement drops off.'
              )}
            </p>

            <LockedPreview>
              <BlurredContent>
                <Masthead>
                  <MastheadEyebrow>
                    {t('FeedbackDigestComplete', 'FEEDBACK DIGEST · COMPLETE')} · AI-GENERATED
                  </MastheadEyebrow>
                  <StatLine>
                    <span>
                      <b>24</b> {t('Comments', 'comments')}
                    </span>
                    <span>
                      <b>6</b> {t('Readers', 'readers')}
                    </span>
                    <span>
                      <b>4.2</b> {t('AvgRating', 'avg rating')}
                    </span>
                  </StatLine>
                </Masthead>
                <SectionCard $open>
                  <SectionHead>
                    <SectionTitle>
                      <SectionIcon $bg="#eef2f7" $color="#41505f">
                        ✦
                      </SectionIcon>
                      {t('ExecutiveSummary', 'Executive summary')}
                    </SectionTitle>
                  </SectionHead>
                  <SectionBody>
                    {t(
                      'FeedbackDigestSamplePlaceholder',
                      'Readers loved the pacing in the middle act but flagged confusion around the antagonist reveal in chapter 12…'
                    )}
                  </SectionBody>
                </SectionCard>
              </BlurredContent>
              <LockOverlay>
                <Icon name="lock" size="large" />
                <strong>
                  {t('UnlockTheFullDigest', 'Unlock the full digest')}
                </strong>
                {editable && hasEnough && (
                  <Button
                    color="teal"
                    loading={isStarting}
                    onClick={startRun}
                    content={t(
                      'RunFeedbackDigest',
                      'Run Feedback Digest — {{n}} credits',
                      { n: digestCost }
                    )}
                  />
                )}
              </LockOverlay>
            </LockedPreview>
          </Segment>
        )}

        {/* Running */}
        {isRunning && (
          <Segment style={{ marginTop: '14px' }}>
            <Icon name="spinner" loading />
            {t(
              'SynthesizingBackground',
              "Synthesizing feedback for '{{title}}' — background job, you can leave this page and come back",
              { title: currentBook.title }
            )}
            <Step.Group ordered size="small" fluid style={{ marginTop: '1rem' }}>
              {PHASES.filter(p => p !== 'completed').map(phase => {
                const effectiveStatus = status === 'pending' ? 'aggregating' : status;
                const phaseIndex = PHASES.indexOf(phase);
                const currentIndex = effectiveStatus
                  ? PHASES.indexOf(effectiveStatus)
                  : -1;
                return (
                  <Step
                    key={phase}
                    completed={currentIndex > phaseIndex}
                    active={effectiveStatus === phase}
                  >
                    <Step.Content>
                      <Step.Title>
                        {t(phase, PHASE_LABELS[phase] || phase)}
                      </Step.Title>
                    </Step.Content>
                  </Step>
                );
              })}
            </Step.Group>
            <p style={{ marginTop: '8px', color: '#999', fontSize: '0.9em' }}>
              {t(
                'FeedbackDigestRunSelfHeals',
                "If it stalls, it resets automatically and any reserved credits are refunded — you're only charged for a completed digest."
              )}
            </p>
          </Segment>
        )}

        {/* Completed */}
        {isCompleted && feedbackDigest?.report && (
          <>
            <FeedbackDigestReportView
              report={feedbackDigest.report}
              statsCommentCount={
                feedbackDigest.stats?.commentCount ?? glance?.commentCount
              }
              statsReaderCount={
                feedbackDigest.stats?.readerCount ?? glance?.readerCount
              }
              statsAvgOverallRating={
                feedbackDigest.stats?.avgOverallRating ??
                glance?.avgOverallRating
              }
              creditsCharged={feedbackDigest.creditsCharged}
              onDownload={downloadMarkdown}
            />

            {editable && (
              <Button
                basic
                loading={isStarting}
                disabled={!hasEnough}
                onClick={startRun}
                style={{ marginTop: '14px' }}
                content={t(
                  'RerunOnThisVersion',
                  'Re-run on this version — {{n}} credits',
                  { n: digestCost }
                )}
              />
            )}
          </>
        )}
      </MyCenteredContent>
      <OutOfCreditsModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
};

export default BookFeedbackDigest;
